/**
 * Pure model for the date picker's masked input. No Angular, no date library.
 *
 * Every character position of the displayed string is described by the compiled
 * pattern, so separators are never derived from an index or read back out of the
 * previously rendered string.
 */

export const FieldKind = {
  Year: 'year',
  Month: 'month',
  Day: 'day',
  Hour24: 'hour24',
  Hour12: 'hour12',
  Minute: 'minute',
  DayPeriod: 'dayPeriod',
} as const;
export type FieldKind = (typeof FieldKind)[keyof typeof FieldKind];

export const RangeSide = { Single: 'single', From: 'from', To: 'to' } as const;
export type RangeSide = (typeof RangeSide)[keyof typeof RangeSide];

/** A range side, or every field of the pattern at once. */
export const ALL_SIDES = 'all';
export type SideSelector = RangeSide | typeof ALL_SIDES;

export const PatternPartKind = { Field: 'field', Literal: 'literal' } as const;
export type PatternPartKind = (typeof PatternPartKind)[keyof typeof PatternPartKind];

export interface FieldPart {
  readonly kind: typeof PatternPartKind.Field;
  /** Index into `CompiledPattern.fields` — also the index into the slots array. */
  readonly index: number;
  readonly token: string;
  readonly field: FieldKind;
  readonly width: number;
  readonly min: number;
  readonly max: number;
  readonly placeholder: string;
  readonly start: number;
  readonly end: number;
  readonly side: RangeSide;
}

export interface LiteralPart {
  readonly kind: typeof PatternPartKind.Literal;
  readonly text: string;
  readonly start: number;
  readonly end: number;
}

export type PatternPart = FieldPart | LiteralPart;

export interface CompiledPattern {
  readonly source: string;
  readonly parts: readonly PatternPart[];
  readonly fields: readonly FieldPart[];
  readonly length: number;
  readonly placeholder: string;
  /** Character index -> field index, or null when the position is a literal. */
  readonly fieldIndexAt: readonly (number | null)[];
  readonly hasDate: boolean;
  readonly hasTime: boolean;
  readonly hour12: boolean;
  readonly isRange: boolean;
}

export const DayPeriod = { Am: 'am', Pm: 'pm' } as const;
export type DayPeriod = (typeof DayPeriod)[keyof typeof DayPeriod];

export interface FieldValues {
  year: number | null;
  month: number | null;
  day: number | null;
  hour24: number | null;
  hour12: number | null;
  minute: number | null;
  dayPeriod: DayPeriod | null;
}

export type Caret = readonly [start: number, end: number];

export interface EditResult {
  readonly slots: readonly string[];
  readonly caret: Caret;
}

export const ParseIssue = {
  Incomplete: 'incomplete',
  InvalidDay: 'invalidDay',
  OutOfRange: 'outOfRange',
} as const;
export type ParseIssue = (typeof ParseIssue)[keyof typeof ParseIssue];

export interface ParseResult {
  readonly empty: boolean;
  readonly complete: boolean;
  readonly values: FieldValues;
  readonly date: Date | null;
  readonly issues: readonly ParseIssue[];
}

/**
 * The slice of a locale profile the pure layer needs, passed in rather than imported
 * so `_core` stays free of configuration.
 */
export interface EditOptions {
  /** Characters that advance to the next field, beyond the pattern's own literals. */
  readonly acceptedSeparators: readonly string[];
}

export const DEFAULT_EDIT_OPTIONS: EditOptions = {
  acceptedSeparators: ['/', '.', '-', ' ', ',', ':'],
};

export const EMPTY_FIELD_VALUES: FieldValues = {
  year: null,
  month: null,
  day: null,
  hour24: null,
  hour12: null,
  minute: null,
  dayPeriod: null,
};
